import React from "react";
import { useNavigate } from "react-router-dom";
import { formatPlanPrice, packageOf, usePlans } from "@/hooks/usePlans";
import { longDay } from "@/components/ProUpgrade/proUpgradeModel";
import { GITCHA_PRO_KEEP_PATH, GITCHA_PRO_PATH } from "@/helpers/gitchaProPath";
import "./YourPlans.scss";

export const AGENT_FEED_PATH = "/settings?tab=profile&row=website";
export const BROKERAGE_PLAN_PATH = "/settings?tab=plans&plan=brokerage";

const amountOf = (pkg) => {
  if (pkg?.period === "year" && pkg?.price?.yearly) return `${formatPlanPrice(pkg.price.yearly)} a year`;
  return pkg?.price?.monthly ? `${formatPlanPrice(pkg.price.monthly)} a month` : null;
};

export const planRows = (plans, { withBrokerage = true } = {}) => {
  const pro = packageOf(plans, "pro");
  const feed = packageOf(plans, "agentFeed");
  const brokerage = packageOf(plans, "brokerage");
  const rows = [];
  if (pro) {
    if (pro.status === "covered") {
      rows.push({ key: "pro", name: "Gitcha Pro", status: `Covered by ${pro.coveredBy || plans?.audience?.brokerage?.name || "your brokerage"}` });
    } else if (pro.status === "trial") {
      rows.push({ key: "pro", name: "Gitcha Pro", status: `Free until ${longDay(pro.trialEndsAt)}`, action: "Keep Pro", path: GITCHA_PRO_KEEP_PATH });
    } else if (pro.status === "active") {
      const renews = longDay(pro.renewsAt);
      const parts = [pro.planName && pro.planName !== "Pro Member" ? pro.planName : null, amountOf(pro), renews ? `renews ${String(renews).replace(" ", "\u00a0")}` : null].filter(Boolean);
      rows.push({ key: "pro", name: "Gitcha Pro", status: parts.join(", "), action: "Manage", path: GITCHA_PRO_PATH });
    } else {
      rows.push({
        key: "pro",
        name: "Standard",
        status: pro.trialUsed ? "Free. Gitcha Pro adds the buyer form, videos and share cards." : "Free. Try Gitcha Pro free for 30 days.",
        action: pro.trialUsed ? "See Gitcha Pro" : "Try Gitcha Pro",
        path: GITCHA_PRO_PATH,
      });
    }
  }
  if (feed?.status === "active") {
    rows.push({ key: "agentFeed", name: "Agent website feed", status: amountOf(feed), action: "Manage", path: AGENT_FEED_PATH });
  }
  if (withBrokerage && brokerage?.status === "active") {
    rows.push({ key: "brokerage", name: "Brokerage plan", status: amountOf(brokerage), action: "Manage", path: BROKERAGE_PLAN_PATH });
  }
  return rows;
};

const YourPlans = ({ withBrokerage = true }) => {
  const navigate = useNavigate();
  const query = usePlans(true);
  const rows = planRows(query.data, { withBrokerage });

  return (
    <section className="content-block your-plans" aria-labelledby="your-plans-title">
      <div className="title-sec">
        <h4 id="your-plans-title">Your plans</h4>
      </div>
      {query.isError ? (
        <p className="your-plans__note">
          Your plans did not load.{" "}
          <button type="button" className="your-plans__action" onClick={() => query.refetch()}>
            Try again
          </button>
        </p>
      ) : !query.data ? (
        <div className="your-plans__skeleton" aria-busy="true" aria-label="Loading your plans">
          <span />
          <span />
        </div>
      ) : (
        <ul className="your-plans__list">
          {rows.map((row) => (
            <li key={row.key} className="your-plans__row">
              <span className="your-plans__name">{row.name}</span>
              <span className="your-plans__status">{row.status}</span>
              {row.action ? (
                <button type="button" className="your-plans__action" onClick={() => navigate(row.path)}>
                  {row.action}
                </button>
              ) : (
                <span />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default YourPlans;
